import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Download, ChevronRight, ChevronDown, TrendingUp } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { useMetaAccount } from "@/contexts/MetaAccountContext";
import { useDateRange } from "@/contexts/DateRangeContext";
import { DateRangePicker } from "@/components/meta/DateRangePicker";
import { CampaignTable } from "@/components/meta/CampaignTable";

export default function MetaCampaignsPage() {
  const { selectedAccountId } = useMetaAccount();
  const { dateRange, formatDate } = useDateRange();
  const [, navigate] = useLocation();
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<number>>(new Set());
  const [expandedAdsets, setExpandedAdsets] = useState<Set<number>>(new Set());

  const dateFrom = formatDate(dateRange.from);
  const dateTo = formatDate(dateRange.to);

  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ["/api/meta/accounts", selectedAccountId, "campaigns", dateFrom, dateTo, statusFilter],
    queryFn: async () => {
      if (!selectedAccountId) return [];
      const params = new URLSearchParams({ dateFrom, dateTo });
      if (statusFilter !== "all") params.set("status", statusFilter);
      const res = await fetch(`/api/meta/accounts/${selectedAccountId}/campaigns?${params}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!selectedAccountId,
  });

  const toggleCampaign = (id: number) => {
    const next = new Set(expandedCampaigns);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpandedCampaigns(next);
  };

  const toggleAdset = (id: number) => {
    const next = new Set(expandedAdsets);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpandedAdsets(next);
  };

  const handleExportCsv = () => {
    const headers = ["Campaign,Objective,Status,Spend,ROAS,CPA,CTR,Impressions,Clicks,Purchases"];
    const rows = campaigns.map((c: any) =>
      `"${c.name}",${c.objective || ""},${c.effectiveStatus || ""},${c.metrics.spend},${c.metrics.roas},${c.metrics.cpa},${c.metrics.ctr},${c.metrics.impressions},${c.metrics.clicks},${c.metrics.purchases}`
    );
    const csv = [...headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `campaigns_${dateFrom}_${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!selectedAccountId) {
    return (
      <Layout>
      <div className="p-6" data-testid="meta-campaigns-no-account">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Campaigns</h1>
        <Card className="p-12">
          <div className="flex flex-col items-center text-center">
            <TrendingUp className="h-12 w-12 text-gray-300 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Select an account</h2>
            <p className="text-gray-500">Choose an ad account from the sidebar to view campaigns.</p>
          </div>
        </Card>
      </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="p-6" data-testid="meta-campaigns-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px]" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="PAUSED">Paused</SelectItem>
            </SelectContent>
          </Select>
          <DateRangePicker />
          <Button variant="outline" onClick={handleExportCsv} data-testid="export-csv">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12" />)}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <CampaignTable
              campaigns={campaigns}
              onRowClick={(c) => toggleCampaign(c.id)}
            />

            {campaigns.map((campaign: any) => (
              expandedCampaigns.has(campaign.id) && (
                <ExpandedCampaign
                  key={`exp-${campaign.id}`}
                  campaign={campaign}
                  accountId={selectedAccountId}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  expandedAdsets={expandedAdsets}
                  toggleAdset={toggleAdset}
                />
              )
            ))}
          </CardContent>
        </Card>
      )}
    </div>
    </Layout>
  );
}

function ExpandedCampaign({ campaign, accountId, dateFrom, dateTo, expandedAdsets, toggleAdset }: any) {
  const { data: adsets = [] } = useQuery({
    queryKey: ["/api/meta/accounts", accountId, "campaigns", campaign.id, "adsets", dateFrom, dateTo],
    queryFn: async () => {
      const res = await fetch(`/api/meta/accounts/${accountId}/campaigns/${campaign.id}/adsets?dateFrom=${dateFrom}&dateTo=${dateTo}`);
      if (!res.ok) return [];
      return res.json();
    },
  });

  return (
    <div className="bg-gray-50 border-t px-6 py-3">
      <p className="text-xs font-semibold text-gray-500 mb-2">Ad Sets for: {campaign.name}</p>
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-100">
            <TableHead className="text-xs">Ad Set</TableHead>
            <TableHead className="text-xs">Status</TableHead>
            <TableHead className="text-xs">Spend</TableHead>
            <TableHead className="text-xs">ROAS</TableHead>
            <TableHead className="text-xs">CPA</TableHead>
            <TableHead className="text-xs">CTR</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {adsets.map((as: any) => (
            <>
              <TableRow key={as.id} className="hover:bg-gray-100 cursor-pointer" onClick={() => toggleAdset(as.id)} data-testid={`adset-row-${as.id}`}>
                <TableCell className="text-sm flex items-center gap-1">
                  {expandedAdsets.has(as.id) ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  {as.name}
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className="text-xs">{as.effectiveStatus || "N/A"}</Badge>
                </TableCell>
                <TableCell className="text-sm font-mono">${parseFloat(as.metrics?.spend || "0").toFixed(2)}</TableCell>
                <TableCell className="text-sm font-mono">{parseFloat(as.metrics?.roas || "0").toFixed(2)}x</TableCell>
                <TableCell className="text-sm font-mono">${parseFloat(as.metrics?.cpa || "0").toFixed(2)}</TableCell>
                <TableCell className="text-sm font-mono">{parseFloat(as.metrics?.ctr || "0").toFixed(2)}%</TableCell>
              </TableRow>
              {expandedAdsets.has(as.id) && (
                <TableRow key={`ads-${as.id}`}>
                  <TableCell colSpan={6} className="p-0">
                    <ExpandedAdset adset={as} accountId={accountId} dateFrom={dateFrom} dateTo={dateTo} />
                  </TableCell>
                </TableRow>
              )}
            </>
          ))}
          {adsets.length === 0 && (
            <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-4">No ad sets found</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function ExpandedAdset({ adset, accountId, dateFrom, dateTo }: any) {
  const { data: ads = [] } = useQuery({
    queryKey: ["/api/meta/accounts", accountId, "adsets", adset.id, "ads", dateFrom, dateTo],
    queryFn: async () => {
      const res = await fetch(`/api/meta/accounts/${accountId}/adsets/${adset.id}/ads?dateFrom=${dateFrom}&dateTo=${dateTo}`);
      if (!res.ok) return [];
      return res.json();
    },
  });

  return (
    <div className="bg-white border-t px-8 py-2">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead className="text-xs">Ad</TableHead>
            <TableHead className="text-xs">Headline</TableHead>
            <TableHead className="text-xs">Spend</TableHead>
            <TableHead className="text-xs">Clicks</TableHead>
            <TableHead className="text-xs">CTR</TableHead>
            <TableHead className="text-xs">ROAS</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ads.map((ad: any) => (
            <TableRow key={ad.id} data-testid={`ad-row-${ad.id}`}>
              <TableCell className="text-sm max-w-[150px] truncate">{ad.name}</TableCell>
              <TableCell className="text-sm max-w-[150px] truncate text-gray-500">{ad.headline || "—"}</TableCell>
              <TableCell className="text-sm font-mono">${parseFloat(ad.metrics?.spend || "0").toFixed(2)}</TableCell>
              <TableCell className="text-sm font-mono">{ad.metrics?.clicks || 0}</TableCell>
              <TableCell className="text-sm font-mono">{parseFloat(ad.metrics?.ctr || "0").toFixed(2)}%</TableCell>
              <TableCell className="text-sm font-mono">{parseFloat(ad.metrics?.roas || "0").toFixed(2)}x</TableCell>
            </TableRow>
          ))}
          {ads.length === 0 && (
            <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-4">No ads found</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
